import { useEffect, useRef, useState } from 'react';
import './style.scss';
import { DateTime } from 'luxon';
import ClearButtonComponent from '../Shared/ClearButton';
import { SanitizerService } from '../../services/sanitizer.service';
import { AnalyticsService } from '../../services/analytics-service';
import type { ILine } from '../../models/line.model';
import { ClientSideStorageItems } from '../../services/client-side-storage.service';
import { LanguageService } from '../../services/language.service';
import { LineContractorApiModel } from './models/line-contractor.api-model';
import { LineHelper } from '../../helpers/line.helper';
import type { IAutocomplete } from '../Shared/AutocompleteBase';
import LineAutocomplete, { type ILineAutocompleteState } from '../Shared/LineAutocomplete';
import type { ILineContractor } from '../../models/line-contractor.model';
import ValidationInfo from '../../validation/validation-info';
import useFetch, { Status } from '../../hooks/fetch.hook';
import LineDescription from '../Shared/LineDescription';
import { IServerSideRendered } from '../Shared/Base/server-side-rendered';
import { useTexts } from '../../hooks/texts.hook';
import Alert from '../Shared/Alert';

export interface ILostAndFoundTexts {
  searchLineLabel: string;
  searchLinePlaceholderText: string;
  searchLineNoResultsText: string;
  websiteLinkText: string;
  noLineSelectedText: string;
}

export type ILostAndFoundSettings = object;

type ILostAndFoundProps = IServerSideRendered<ILostAndFoundTexts, ILostAndFoundSettings>;

function processReceivedData(data: LineContractorApiModel[]): ILineContractor[] {
  return data?.map((d) => LineContractorApiModel.mapToBusinessModel(d)).filter((d): d is ILineContractor => d !== undefined) ?? [];
}

function LostAndFound({ texts }: ILostAndFoundProps) {
  // Hooks
  const { t } = useTexts();

  const [lineAutocompleteState, setLineAutocompleteState] = useState<ILineAutocompleteState>({ value: undefined, validationInfo: new ValidationInfo() });
  const preventFetch = !lineAutocompleteState.value;
  const url = !preventFetch ? `/api/lost-and-found/lines/${lineAutocompleteState.value?.gid}/contractors?language=${LanguageService.getCurrentLanguage()}` : undefined;
  const [{ status: getLineContractorsStatus, data: getLineContractorsData, httpResponseStatusCode: getLineContractorsHttpResponseStatusCode }] = useFetch<LineContractorApiModel[]>(url, preventFetch);
  const lineAutocompleteRef = useRef<IAutocomplete>(null);

  const contractors = getLineContractorsData ? processReceivedData(getLineContractorsData) : [];

  // Hide all future lines
  function hideFutureLines(line: ILine | undefined): boolean {
    return line?.appliesFrom ? line.appliesFrom <= DateTime.now() : true;
  }

  // See NV-3700, Hide all lines for Västtrafik/Göteborgs Spårvägar Ersättningstrafik
  function hideSpecifiedLines(line: ILine): boolean {
    const gsersContractorId = 18; // Göteborgs Spårvägar Ersättningstrafik
    const vtersContractorId = 315; // Västtrafik Ersättningstrafik
    return line.contractorId !== gsersContractorId && line.contractorId !== vtersContractorId;
  }

  function onLineAutocompleteSelect(line: ILine | undefined) {
    setLineAutocompleteState({
      value: line,
      validationInfo: new ValidationInfo(),
    });
    if (line && lineAutocompleteRef.current) {
      lineAutocompleteRef.current.addSelectedItemToHistory(line);
      AnalyticsService.addEvent(AnalyticsService.LostAndFound, 'Sökalternativ - Linje', `${LineHelper.getLineNameAndDirection(line)}, ${line.gid}`);
    }
  }

  const onClearClick = () => {
    lineAutocompleteRef.current?.clear();
    setLineAutocompleteState({
      value: undefined,
      validationInfo: new ValidationInfo(),
    });
    AnalyticsService.addEvent(AnalyticsService.LostAndFound, 'Klick på återställ', '');
  };

  useEffect(() => {
    if (getLineContractorsStatus === Status.failed && getLineContractorsHttpResponseStatusCode === 404 && lineAutocompleteState.value) {
      lineAutocompleteRef.current?.removeFromHistory?.(lineAutocompleteState.value);
    }
  }, [getLineContractorsStatus, getLineContractorsHttpResponseStatusCode, lineAutocompleteState.value]);

  return (
    <div>
      <div className="lost-and-found">
        <div className="lost-and-found__line shadow-sm rounded py-sm-3 mb-4">
          <div className="mb-3 px-sm-3">
            <LineAutocomplete
              id="line-autocomplete"
              /* @ts-expect-error ts(2322) */
              ref={lineAutocompleteRef}
              labelText={texts.searchLineLabel}
              labelClassName="form-label"
              placeholderText={texts.searchLinePlaceholderText}
              noResultsText={texts.searchLineNoResultsText}
              historyProps={{ localStorageItem: ClientSideStorageItems.LocalStorage.LineAutocompleteHistory }}
              hideFutureLines={true}
              listAriaLabelledBy="lost-and-found-line-label"
              filterResults={(l) => hideFutureLines(l) && hideSpecifiedLines(l)}
              state={lineAutocompleteState}
              onSelect={onLineAutocompleteSelect}
            />
          </div>
          <div className="px-sm-3">
            <ClearButtonComponent onClick={onClearClick}></ClearButtonComponent>
          </div>
        </div>
        <h2>{t('Kontaktuppgifter', 'Contact information')}</h2>
        {lineAutocompleteState.value && getLineContractorsStatus === Status.success && (
          <div className="lost-and-found__result-box p-3 mb-3">
            <LineDescription line={lineAutocompleteState.value} showTransportMode={true} showDirectionName={true} className="mb-3" />
            {contractors.map((contractor) => (
              <div key={contractor.name} className="last-child-border-bottom-none">
                <div className="mb-1">
                  {t('Körs av', 'Company')} {contractor.name}
                </div>
                {contractor.telephoneNumber && (
                  <div className="mb-1">
                    <a
                      href={`tel:${contractor.telephoneNumber}`}
                      onClick={() => AnalyticsService.addEvent(AnalyticsService.LinksCategory, AnalyticsService.PhoneNumberAction, contractor.telephoneNumber)}
                    >
                      {contractor.telephoneNumber}
                    </a>
                  </div>
                )}
                {contractor.phoneHours && (
                  <div className="mb-1">
                    {t('Telefontid', 'Phone hours')}: {contractor.phoneHours}
                  </div>
                )}
                {contractor.emailAddress && (
                  <div className="mb-1">
                    <a
                      href={`mailto:${contractor.emailAddress}`}
                      onClick={() => AnalyticsService.addEvent(AnalyticsService.LinksCategory, AnalyticsService.EmailAddressAction, contractor.emailAddress)}
                    >
                      {contractor.emailAddress}
                    </a>
                  </div>
                )}
                {contractor.website && (
                  <div className="mb-1">
                    <a href={contractor.website} onClick={() => AnalyticsService.addEvent(AnalyticsService.LinksCategory, AnalyticsService.ExternalLinkAction, contractor.website)}>
                      {texts.websiteLinkText || contractor.website}
                    </a>
                  </div>
                )}
                {contractor.comment && <div className="mb-1">{contractor.comment}</div>}
              </div>
            ))}
          </div>
        )}
        {!lineAutocompleteState.value && <div dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(texts.noLineSelectedText) }}></div>}
        <Alert
          show={getLineContractorsStatus === Status.failed}
          heading={getLineContractorsHttpResponseStatusCode === 404 ? t('Den valda linjen kan inte hittas', 'The selected line could not be found') : t.generalError}
          closable={false}
        />
      </div>
    </div>
  );
}

export default LostAndFound;